import { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import type { RouteObject } from 'react-router-dom';

import { lazyWithReload } from './lazyWithReload';
import { RequireAuth } from './RequireAuth';

/**
 * Phase Ads — Stage 1 / 2 / 3 共用 routes(spread 進 `routes.tsx`)
 * -----------------------------------------------------------------------------
 * 對齊 Frontend Developer panel 拍板:
 *   - 拆檔避免 `routes.tsx` 膨脹 50%(對齊既有風格,不另開 group 元件)
 *   - 6 個 Stage 1 page 都用 `<RequireAuth>`(LOGIN 後可進);**進場守衛**(沒帳號 →
 *     redirect)放在 page 內部用 cold-start fetch 處理,不在這層加 `<RequireAdsAccount>`
 *     (避免每次 navigate 重 fetch)
 *
 * **Stage 2 / 3 進場**:加 `/ads/assets/:assetId/publish`(Stage 2)/ `/ads/assets/:assetId/performance`
 * + `/ads/my-published-assets`(Stage 3)。
 *
 * Code split — 全 lazy + Suspense fallback(對齊 R34 既有 code split pattern;雖然
 * `routes.tsx` 既有 page 是 eager import,但 ads 子樹 first paint 不關鍵 + 9 個 page
 * + Recharts ~100KB 值得分塊)。
 */

const AdsConnectPage = lazyWithReload('AdsConnectPage', () =>
  import('@/features/ads/pages/AdsConnectPage').then((m) => ({ default: m.AdsConnectPage })),
);
const AdsConnectingPage = lazyWithReload('AdsConnectingPage', () =>
  import('@/features/ads/pages/AdsConnectingPage').then((m) => ({
    default: m.AdsConnectingPage,
  })),
);
const AdsAccountsSelectPage = lazyWithReload('AdsAccountsSelectPage', () =>
  import('@/features/ads/pages/AdsAccountsSelectPage').then((m) => ({
    default: m.AdsAccountsSelectPage,
  })),
);
const AdsAccountsPage = lazyWithReload('AdsAccountsPage', () =>
  import('@/features/ads/pages/AdsAccountsPage').then((m) => ({ default: m.AdsAccountsPage })),
);
const AdsSyncPage = lazyWithReload('AdsSyncPage', () =>
  import('@/features/ads/pages/AdsSyncPage').then((m) => ({ default: m.AdsSyncPage })),
);
const AdsReportsPage = lazyWithReload('AdsReportsPage', () =>
  import('@/features/ads/pages/AdsReportsPage').then((m) => ({ default: m.AdsReportsPage })),
);
// R325 — 跨來源素材分群(docs/117):平台素材有沒有真的被投放
const AssetGroupsPage = lazyWithReload('AssetGroupsPage', () =>
  import('@/features/ads/pages/AssetGroupsPage').then((m) => ({ default: m.AssetGroupsPage })),
);
// R48 D3 Tier 4 — M-07 + M-08 整合 wizard(純 UI scaffold;BE Wave 2 publish endpoint
// ship 後 wire `services.adsService.bulkPublish(...)`)
const AdsImportWizardPage = lazyWithReload('AdsImportWizardPage', () =>
  import('@/features/ads/pages/AdsImportWizardPage').then((m) => ({
    default: m.AdsImportWizardPage,
  })),
);

/** Suspense fallback 需要 hook 取 t,抽成小元件(route 陣列是 module scope,不能直接用 hook)。 */
function AdsSuspenseFallback() {
  const { t } = useTranslation('ads');
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        background: 'var(--ink-950)',
        color: 'var(--ink-500)',
        fontSize: 'var(--text-base)',
      }}
    >
      {t('nav.loading')}
    </div>
  );
}

function AdsRouteWrapper({ children }: { children: React.ReactNode }) {
  return (
    <RequireAuth>
      <Suspense fallback={<AdsSuspenseFallback />}>{children}</Suspense>
    </RequireAuth>
  );
}

export const adsRoutes: RouteObject[] = [
  {
    path: '/ads/connect',
    element: (
      <AdsRouteWrapper>
        <AdsConnectPage />
      </AdsRouteWrapper>
    ),
  },
  {
    path: '/ads/connecting',
    element: (
      <AdsRouteWrapper>
        <AdsConnectingPage />
      </AdsRouteWrapper>
    ),
  },
  {
    path: '/ads/accounts/select',
    element: (
      <AdsRouteWrapper>
        <AdsAccountsSelectPage />
      </AdsRouteWrapper>
    ),
  },
  {
    path: '/ads/accounts',
    element: (
      <AdsRouteWrapper>
        <AdsAccountsPage />
      </AdsRouteWrapper>
    ),
  },
  {
    path: '/ads/sync',
    element: (
      <AdsRouteWrapper>
        <AdsSyncPage />
      </AdsRouteWrapper>
    ),
  },
  {
    path: '/ads/reports',
    element: (
      <AdsRouteWrapper>
        <AdsReportsPage />
      </AdsRouteWrapper>
    ),
  },
  {
    path: '/ads/asset-groups',
    element: (
      <AdsRouteWrapper>
        <AssetGroupsPage />
      </AdsRouteWrapper>
    ),
  },
  {
    // R48 D3 Tier 4 — M-07 + M-08 整合 import wizard(5 step + 整合確認 + 發布結果)
    path: '/ads/import',
    element: (
      <AdsRouteWrapper>
        <AdsImportWizardPage />
      </AdsRouteWrapper>
    ),
  },
];
